import { useState, useEffect, useRef, createContext } from "react";

import { useParams, Link, useNavigate } from "react-router-dom";
import { Button, Row, Col, FormGroup, Input, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import ResumeUpload from "../../../../jobportal/src/components/PortalApplicationContent/ResumeUpload/ResumeUpload";
import "./ApplicationForm.scss";
import { resetJobPortalResume } from "../../../../jobportal/src/store/job/action";
import useImportCandidate from "@workspace/candidate/src/pages/CandidateResumeParse/useImportCandidate";

const getInitialValues = (resumeData) => {
  const initialFormikValues = {
    firstName: "",
    lastName: "",
    gender: "",
    mobile: "",
    email: "",
    currentLocation: "",
    totalExperience: "",
    relevantExperience: "",
    currentSalary: "",
    expectedSalary: "",
    noticePeriod: "",
    reasonForChange: "",
    referrerName: "",
    candidateSource: "",
    remark: "",
    resume: null,
  };
  return { ...initialFormikValues, ...resumeData };
};

const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  gender: Yup.string().required("Gender is required"),
  mobile: Yup.number().required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  currentLocation: Yup.string().required("Current location is required"),
  totalExperience: Yup.string().required("Total experience is required"),
  relevantExperience: Yup.string().required("Relevant experience is required"),
  currentSalary: Yup.string().required("Current salary is required"),
  expectedSalary: Yup.string().required("Expected salary is required"),
  noticePeriod: Yup.string().required("Notice period is required"),
  reasonForChange: Yup.string().required("Reason for change is required"),
  candidateSource: Yup.string().required("Candidate source is required"),
  resume: Yup.mixed()
    .required("Resume is required")
    .test(
      "fileSize",
      "File size should be less than 2MB",
      (value) => value && value.size <= 2097152
    ),
});

export default function ApplicationForm() {
  const { importCandidate } = useImportCandidate();

  const { jobId } = useParams();
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);

  const [candidateData, setCandidateData] = useState(null);
  const [candidateFile, setCandidateFile] = useState(null);

  // Get global state variables from redux store
  const resumeData = useSelector(
    (state) => state.JobPortalJobReducer.resumeData
  );
  const loading = useSelector((state) => state.JobPortalJobReducer.loading);
  const success = useSelector((state) => state.JobPortalJobReducer.success);
  const error = useSelector((state) => state.JobPortalJobReducer.error);
  const errorMsg = useSelector((state) => state.JobPortalJobReducer.errorMsg);
  const jobRaw = useSelector(
    (state) => state.JobPortalJobReducer.job.submissionData
  );

  // Get initial values for formik form
  const initialValues = getInitialValues(filterResumeData(resumeData));

  // Reset form fields on component mount
  useEffect(() => {
    if (formikRef.current) {
      dispatch(resetJobPortalResume()).then(() => {
        formikRef.current.resetForm();
      });
    }
  }, []);

  // Display error message if form submission fails
  useEffect(() => {
    if (error) {
      toast.error(errorMsg);
    }
  }, [error, errorMsg]);

  // Map resume data to match application form fields
  function filterResumeData(resumeData) {
    if (Object.keys(resumeData).length === 0) return {};
    return {
      firstName: resumeData.firstName,
      lastName: resumeData.lastName,
      gender: resumeData.gender,
      mobile: `${resumeData.mobile}`,
      // mobile: `${resumeData.mobileExt}${resumeData.mobile}`,
      email: resumeData.email,
      currentLocation: resumeData.currentLocation,
      totalExperience: resumeData.totalExperience,
      relevantExperience: resumeData.relevantExperience,
      currentSalary: resumeData.currentSalary,
      expectedSalary: resumeData.expectedSalary,
      noticePeriod: resumeData.noticePeriod,
      reasonForChange: resumeData.reasonToLeave,
      referrerName: resumeData.referrerName,
      candidateSource: resumeData.candidateSource,
      remark: resumeData.profile,
      resume: file,
    };
  }

  // Map form fields value to resume data
  function mapFormikDataToResumeData(formikData) {
    return {
      ...resumeData,
      firstName: formikData.firstName,
      lastName: formikData.lastName,
      gender: formikData.gender,
      mobile: formikData.mobile,
      email: formikData.email,
      currentLocation: formikData.currentLocation,
      totalExperience: formikData.totalExperience,
      relevantExperience: formikData.relevantExperience,
      currentSalary: formikData.currentSalary,
      expectedSalary: formikData.expectedSalary,
      noticePeriod: formikData.noticePeriod,
      reasonToLeave: formikData.reasonForChange,
      referrerName: formikData.referrerName,
      candidateSource: formikData.candidateSource,
      profile: formikData.remark,
    };
  }

  // onSubmit
  const handleSubmit = (values) => {
    const overridenResumeData = mapFormikDataToResumeData(values);

    // Populate formData
    const actionData = new FormData();
    for (const key in values) {
      actionData.append(key, values[key]);
    }

    actionData.append("candidateData", JSON.stringify(overridenResumeData));

    const fileName = file.name;
    const fileNameNoExt = fileName.split(".").slice(0, -1).join(".");
    const fileObject = {
      file: file,
      name: fileNameNoExt,
      nameNoExt: fileNameNoExt,
    };

    setCandidateData([JSON.parse(actionData.get("candidateData"))]);

    setCandidateFile([fileObject]);
  };

  useEffect(() => {
    if (candidateData != null && candidateFile != null) {
      importCandidate(candidateData, candidateFile, true);
    }
  }, [candidateData, candidateFile]);

  return (
    <div className="px-4 pt-3 bg-white rounded appform-container">
      {loading && (
        <div className="spinner-overlay w-100">
          <Spinner className="m-5" color="primary">
            Loading...
          </Spinner>
        </div>
      )}
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
          <Form className="mb-3" onSubmit={handleSubmit}>
            <Row className="mx-0 pb-3 border-bottom border-1 border-light">
              <Col className="d-flex align-items-center justify-content-between px-0">
                <h5 className="mb-0">Profile details</h5>
                <div className="d-flex gap-3">
                  {/* <Button
                    type="button"
                    className="btn-primary"
                    onClick={() =>
                      //  setIsVideoModalOpen(true)
                      setOffVideoCanvasOpen(true)
                    }
                  >
                    Video
                  </Button> */}
                  <Button
                    className="cancel-btn"
                    onClick={() => navigate("/job-portal/home")}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="submit-btn">
                    Submit
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="mx-0 border-bottom border-1 border-light">
              <Col className="px-0">
                {/* UPLOAD RESUME */}
                <Field
                  component={ResumeUpload}
                  name="resume"
                  file={file}
                  setFile={setFile}
                  errorMsg={
                    <ErrorMessage
                      name="resume"
                      component="div"
                      className="text-danger"
                    />
                  }
                />
              </Col>
            </Row>
            <Row className="mx-0 mt-3 border-bottom border-1 border-light">
              <Col className="mx-auto px-0 max-width-768">
                <h6 className="mb-4">Basic Information</h6>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="firstName">
                        First Name<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="firstName"
                        className="form-control"
                        placeholder="Enter"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="firstName"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="lastName">
                        Last Name<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="lastName"
                        className="form-control"
                        placeholder="Enter"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="lastName"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="gender">
                        Gender<span className="text-danger">*</span>
                      </Label>
                      <Input
                        style={{ border: "1px solid #f5f5f5" }}
                        component="select"
                        tag={Field}
                        name="gender"
                        type="select"
                      >
                        <option value="" label="Select" />
                        <option value="Male" label="Male" />
                        <option value="Female" label="Female" />
                        <option value="Others" label="Others" />
                      </Input>
                      <ErrorMessage
                        name="gender"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="mobile">
                        Phone<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        tag={Field}
                        name="mobile"
                        className="form-control"
                        placeholder="Enter Phone Number"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="mobile"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="email">
                        Email<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="email"
                        tag={Field}
                        name="email"
                        className="form-control"
                        placeholder="Enter Email"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="currentLocation">
                        Current Location<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="currentLocation"
                        className="form-control"
                        placeholder="Enter Location"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="currentLocation"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mx-0 mt-3 border-bottom border-1 border-light">
              <Col className="mx-auto px-0 max-width-768">
                <h6 className="mb-4">Professional Information</h6>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="totalExperience">
                        Total Experience (in years)
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="totalExperience"
                        className="form-control"
                        placeholder="Enter Total Experience"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="totalExperience"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="relevantExperience">
                        Relevant Experience (in years)
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="relevantExperience"
                        className="form-control"
                        placeholder="Enter Relevant Experience"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="relevantExperience"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="currentSalary">
                        Current Salary<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="currentSalary"
                        className="form-control"
                        placeholder="SGD $4500"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="currentSalary"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="expectedSalary">
                        Expected Salary<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="expectedSalary"
                        className="form-control"
                        placeholder="SGD $5000"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="expectedSalary"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="noticePeriod">
                        Notice Period<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="noticePeriod"
                        className="form-control"
                        placeholder="Enter Notice Period"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="noticePeriod"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="reasonForChange">
                        Reason For Change<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="reasonForChange"
                        className="form-control"
                        placeholder="Enter Reason for Change"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="reasonForChange"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mx-0 gap-3">
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="referrerName">
                        Referrer's Name (if applicable)
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="referrerName"
                        className="form-control"
                        placeholder="Enter Referrer Name"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="referrerName"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col className="px-0">
                    <FormGroup>
                      <Label htmlFor="candidateSource">
                        Candidate Source<span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        tag={Field}
                        name="candidateSource"
                        className="form-control"
                        placeholder="Enter Candidate Source"
                        style={{ border: "1px solid #f5f5f5" }}
                      />
                      <ErrorMessage
                        name="candidateSource"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mx-0 mt-3 border-bottom border-1 border-light">
              <Col className="mx-auto px-0 max-width-768">
                <FormGroup>
                  <Label htmlFor="remark">Remark</Label>
                  <Input
                    component="textarea"
                    tag={Field}
                    name="remark"
                    className="form-control"
                    placeholder="Additional Info"
                    style={{ border: "1px solid #f5f5f5" }}
                  />
                  <ErrorMessage
                    name="remark"
                    component="div"
                    className="text-danger"
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
}
